import { useState, useMemo, useContext, useEffect } from 'react';
import {
    Card,
    Grid,
    CircularProgress,
    Autocomplete,
    TextField,
    MenuItem,
    Modal,
    Backdrop,
    Fade,
    Icon,
    Tooltip
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import DataTableCustom from 'components/Tables/DataTableCustom';
import { useLazyQuery, useApolloClient, gql } from '@apollo/client';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import * as XLSX from 'xlsx';
import { UserContext } from 'context/user';

dayjs.locale('es');
const roundMoney = (value) => Number((Number(value) || 0).toFixed(2));
const saldoPendiente = (value) => Math.max(-Number(value || 0), 0);
const FECHA_INICIO_REPORTE = '2020-01-01';

// ===================================
// GRAPHQL QUERIES
// ===================================

const GET_CLIENTES_AUTOCOMPLETE = gql`
    query ClientesParaCuentasPorCobrar($search: String, $tipoCliente: String, $limit: Int) {
        clientesParaCuentasPorCobrar(search: $search, tipoCliente: $tipoCliente, limit: $limit) {
            id
            nombre
            tipoCliente
            esEvento
        }
    }
`;

const GET_REPORTE_CUENTAS_POR_COBRAR = gql`
    query ReporteCuentasPorCobrar($fechaInicio: String!, $fechaFin: String!, $tipoCliente: String, $idCliente: Int, $idSede: Int) {
        reporteCuentasPorCobrar(fechaInicio: $fechaInicio, fechaFin: $fechaFin, tipoCliente: $tipoCliente, idCliente: $idCliente, idSede: $idSede) {
            deudaPorMes {
                periodo
                totalDeuda
            }
            deudaAcumulada {
                periodo
                totalAcumulado
            }
            totalesPorPeriodo {
                periodo
                totalInicio
                totalDeuda
                totalPagos
                totalBecas
                totalBecasRegistradas
                total
                acumulado
                saldoBeca
                detallesPorTipo {
                    tipoCliente
                    totalInicio
                    deudas
                    pagos
                    becas
                    becasRegistradas
                    total
                    acumulado
                    saldoBeca
                }
            }
            totalesPorCliente {
                idCliente
                nombre
                tipoCliente
                totalInicio
                totalDeuda
                totalPagos
                totalBecas
                totalBecasRegistradas
                total
                acumulado
                saldoBeca
                detallesPorPeriodo {
                    periodo
                    totalInicio
                    totalDeuda
                    totalPagos
                    totalBecas
                    totalBecasRegistradas
                    total
                    acumulado
                    saldoBeca
                }
            }
            rankingDeudores {
                posicion
                idCliente
                nombre
                tipoCliente
                deudaTotal
                ultimoPago
                diasSinPagar
            }
            analisisMorosidad {
                rango0_30 {
                    cantidadClientes
                    totalDeuda
                    porcentaje
                }
                rango31_60 {
                    cantidadClientes
                    totalDeuda
                    porcentaje
                }
                rango61_90 {
                    cantidadClientes
                    totalDeuda
                    porcentaje
                }
                rango90_mas {
                    cantidadClientes
                    totalDeuda
                    porcentaje
                }
            }
            tasaRecuperacion {
                periodo
                deudaGenerada
                pagosRecibidos
                tasaRecuperacion
            }
        }
    }
`;

const GET_DETALLE_MOVIMIENTOS = gql`
    query DetalleMovimientos($periodo: String!, $tipoCliente: String!, $fechaInicio: String!, $fechaFin: String!, $idCliente: Int) {
        detalleMovimientos(periodo: $periodo, tipoCliente: $tipoCliente, fechaInicio: $fechaInicio, fechaFin: $fechaFin, idCliente: $idCliente) {
            fecha
            nombre
            tipoCliente
            responsable
            deuda
            pago
            beca
        }
    }
`;

const tiposDeResponsable = {
    1: "IVA Responsable Inscripto",
    2: "IVA Responsable no Inscripto",
    3: "IVA no Responsable",
    4: "IVA Sujeto Exento",
    5: "Consumidor Final",
    6: "Responsable Monotributo",
    7: "Sujeto no Categorizado",
    8: "Proveedor del Exterior",
    9: "Cliente del Exterior",
    10: "IVA Liberado – Ley Nº 19.640",
    11: "IVA Responsable Inscripto – Agente de Percepción",
    12: "Pequeño Contribuyente Eventual",
    13: "Monotributista Social",
    14: "Pequeño Contribuyente Eventual Social"
};

function ReporteCuentasxCobrar() {
    // ===================================
    // STATE
    // ===================================
    const fechaInicio = FECHA_INICIO_REPORTE;
    const [fechaFin, setFechaFin] = useState(dayjs().format('YYYY-MM-DD'));
    const [tipoCliente, setTipoCliente] = useState(null);
    const [agruparPor, setAgruparPor] = useState('Periodo');
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [clienteBusqueda, setClienteBusqueda] = useState('');
    const [clienteBusquedaDebounced, setClienteBusquedaDebounced] = useState('');
    const [clientesOptions, setClientesOptions] = useState([]);
    const [clienteAutocompleteOpen, setClienteAutocompleteOpen] = useState(false);
    const [reporteData, setReporteData] = useState(null);
    const [filtrosAplicados, setFiltrosAplicados] = useState(null);
    const [loadingReporteExcel, setLoadingReporteExcel] = useState(false);
    const [modoRanking, setModoRanking] = useState('PERIODO');
    const [modalDetalle, setModalDetalle] = useState({ open: false, periodo: null, tipoCliente: null, idCliente: null, nombre: null });
    const { user } = useContext(UserContext);
    const apolloClient = useApolloClient();

    // ===================================
    // QUERIES
    // ===================================
    const [fetchClientes, { loading: loadingClientes }] = useLazyQuery(GET_CLIENTES_AUTOCOMPLETE, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const next = Array.isArray(data?.clientesParaCuentasPorCobrar)
                ? data.clientesParaCuentasPorCobrar
                : [];
            setClientesOptions((prev) => {
                if (!clienteSeleccionado) return next;
                const alreadyExists = next.some((item) =>
                    item.id === clienteSeleccionado.id
                    && item.esEvento === clienteSeleccionado.esEvento
                    && item.tipoCliente === clienteSeleccionado.tipoCliente
                );
                return alreadyExists ? next : [clienteSeleccionado, ...next];
            });
        },
    });

    const [fetchReporte, { loading: loadingReporte }] = useLazyQuery(GET_REPORTE_CUENTAS_POR_COBRAR, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setReporteData(data.reporteCuentasPorCobrar);
        }
    });
    const [fetchDetalle, { data: detalleData, loading: loadingDetalle }] = useLazyQuery(GET_DETALLE_MOVIMIENTOS, {
        fetchPolicy: 'network-only'
    });
    const idSedeActual = parseInt(user?.sede?.id, 10) || null;

    useEffect(() => {
        const timer = setTimeout(() => {
            setClienteBusquedaDebounced(String(clienteBusqueda || '').trim());
        }, 250);
        return () => clearTimeout(timer);
    }, [clienteBusqueda]);

    useEffect(() => {
        if (!clienteAutocompleteOpen) return;
        if (!tipoCliente) {
            setClientesOptions([]);
            return;
        }

        if (clienteBusquedaDebounced.length < 2) {
            setClientesOptions(clienteSeleccionado ? [clienteSeleccionado] : []);
            return;
        }

        fetchClientes({
            variables: {
                search: clienteBusquedaDebounced,
                tipoCliente,
                limit: 50,
            },
        });
    }, [
        clienteAutocompleteOpen,
        tipoCliente,
        clienteBusquedaDebounced,
        clienteSeleccionado,
        fetchClientes,
    ]);

    const filtrosActuales = useMemo(() => ({
        fechaInicio,
        fechaFin,
        tipoCliente: tipoCliente || 'Todos',
        agruparPor,
        idCliente: clienteSeleccionado?.id || null,
        tipoClienteCliente: clienteSeleccionado?.tipoCliente || null,
        esEventoCliente: Boolean(clienteSeleccionado?.esEvento),
        cliente: clienteSeleccionado?.nombre || 'Todos',
        idSede: idSedeActual,
    }), [fechaInicio, fechaFin, tipoCliente, agruparPor, clienteSeleccionado, idSedeActual]);

    // ===================================
    // HANDLERS
    // ===================================
    const handleFiltrar = () => {
        if (!fechaFin || !tipoCliente) return;

        fetchReporte({
            variables: {
                fechaInicio,
                fechaFin,
                tipoCliente,
                idCliente: clienteSeleccionado?.id || null,
                idSede: idSedeActual
            }
        });
        setFiltrosAplicados(filtrosActuales);
    };

    const getSaldoPeriodoNetoCliente = (cliente) => {
        const detalles = Array.isArray(cliente?.detallesPorPeriodo) ? cliente.detallesPorPeriodo : [];
        if (!detalles.length) return Number(cliente?.total || 0);
        return roundMoney(detalles.reduce((acc, detalle) => acc + Number(detalle?.total || 0), 0));
    };

    const getSaldoHistoricoCliente = (cliente) => {
        const detalles = Array.isArray(cliente?.detallesPorPeriodo) ? cliente.detallesPorPeriodo : [];
        if (!detalles.length) return Number(cliente?.acumulado || 0);
        const ultimoDetalle = detalles[detalles.length - 1];
        return Number(ultimoDetalle?.acumulado ?? cliente?.acumulado ?? 0);
    };

    const buildRawRowsForExcel = (data) => {
        const rows = [];
        (data?.totalesPorCliente || []).forEach((cliente) => {
            const detalles = cliente?.detallesPorPeriodo || [];
            if (!detalles.length) {
                const saldoPeriodoNeto = Number(cliente?.total || 0);
                const deudaAcumuladaNeta = Number(cliente?.acumulado || 0);

                rows.push({
                    Periodo: 'Sin detalle',
                    IdCliente: cliente?.idCliente || null,
                    Cliente: cliente?.nombre || '',
                    TipoCliente: cliente?.tipoCliente || '',
                    SaldoInicioNeto: Number(cliente?.totalInicio || 0),
                    DeudaPeriodo: Number(cliente?.totalDeuda || 0),
                    PagosPeriodo: Number(cliente?.totalPagos || 0),
                    BecaRegistradaPeriodo: Number(cliente?.totalBecasRegistradas || 0),
                    BecaAplicadaPeriodo: Number(cliente?.totalBecas || 0),
                    SaldoBeca: Number(cliente?.saldoBeca || 0),
                    SaldoPeriodoNeto: saldoPeriodoNeto,
                    SaldoPeriodo: roundMoney(saldoPendiente(saldoPeriodoNeto)),
                    SaldoAcumuladoNeto: deudaAcumuladaNeta,
                    SaldoAcumuladoPendiente: roundMoney(saldoPendiente(deudaAcumuladaNeta)),
                });
                return;
            }

            detalles.forEach((detalle) => {
                const saldoPeriodoNeto = Number(detalle?.total || 0);
                const deudaAcumuladaNeta = Number(detalle?.acumulado || 0);

                rows.push({
                    Periodo: detalle?.periodo || null,
                    IdCliente: cliente?.idCliente || null,
                    Cliente: cliente?.nombre || '',
                    TipoCliente: cliente?.tipoCliente || '',
                    SaldoInicioNeto: Number(detalle?.totalInicio || 0),
                    DeudaPeriodo: Number(detalle?.totalDeuda || 0),
                    PagosPeriodo: Number(detalle?.totalPagos || 0),
                    BecaRegistradaPeriodo: Number(detalle?.totalBecasRegistradas || 0),
                    BecaAplicadaPeriodo: Number(detalle?.totalBecas || 0),
                    SaldoBeca: Number(detalle?.saldoBeca || 0),
                    SaldoPeriodoNeto: saldoPeriodoNeto,
                    SaldoAcumuladoNeto: deudaAcumuladaNeta,
                    SaldoAcumuladoPendiente: roundMoney(saldoPendiente(deudaAcumuladaNeta)),
                });
            });
        });
        return rows;
    };

    const buildResumenRowsForExcel = ({ resumenPorTipo }) => {
        const tipos = ['Funcionario', 'No Funcionario', 'Evento'];
        let totalGeneralSaldoPeriodo = 0;
        let totalGeneralDeudaAcumulada = 0;
        const rows = [];

        tipos.forEach((tipo) => {
            const resumen = resumenPorTipo?.[tipo];
            if (!resumen) return;

            const saldoPeriodoPendiente = roundMoney(resumen.totalSaldoPeriodoPendiente || 0);
            const deudaAcumuladaPendiente = roundMoney(resumen.totalDeudaAcumuladaPendiente || 0);

            totalGeneralSaldoPeriodo = roundMoney(totalGeneralSaldoPeriodo + saldoPeriodoPendiente);
            totalGeneralDeudaAcumulada = roundMoney(totalGeneralDeudaAcumulada + deudaAcumuladaPendiente);

            rows.push({
                concepto: `${tipo} - Saldo pendiente del periodo`,
                monto: saldoPeriodoPendiente,
            });
            rows.push({
                concepto: `${tipo} - Saldo acumulado pendiente`,
                monto: deudaAcumuladaPendiente,
            });
        });

        rows.push({
            concepto: 'TOTAL GENERAL - Saldo pendiente del periodo',
            monto: roundMoney(totalGeneralSaldoPeriodo),
        });
        rows.push({
            concepto: 'TOTAL GENERAL - Saldo acumulado pendiente',
            monto: roundMoney(totalGeneralDeudaAcumulada),
        });

        return rows;
    };

    const buildRowsByTipoForExcel = (data, tipoClienteFiltro = null) => {
        const rows = [];
        let totalSaldoPeriodoPendiente = 0;
        let totalDeudaAcumuladaPendiente = 0;

        (data?.totalesPorCliente || []).forEach((cliente) => {
            if (tipoClienteFiltro && cliente?.tipoCliente !== tipoClienteFiltro) return;

            const saldoPeriodoNeto = roundMoney(getSaldoPeriodoNetoCliente(cliente));
            const deudaAcumuladaNeta = roundMoney(getSaldoHistoricoCliente(cliente));
            const saldoPeriodoPendiente = roundMoney(saldoPendiente(saldoPeriodoNeto));
            const deudaAcumuladaPendiente = roundMoney(saldoPendiente(deudaAcumuladaNeta));

            if (saldoPeriodoPendiente <= 0 && deudaAcumuladaPendiente <= 0) return;

            totalSaldoPeriodoPendiente = roundMoney(totalSaldoPeriodoPendiente + saldoPeriodoPendiente);
            totalDeudaAcumuladaPendiente = roundMoney(totalDeudaAcumuladaPendiente + deudaAcumuladaPendiente);
            rows.push([
                cliente?.nombre || '',
                saldoPeriodoPendiente,
                deudaAcumuladaPendiente,
            ]);
        });

        return {
            rows,
            totalSaldoPeriodoPendiente,
            totalDeudaAcumuladaPendiente,
        };
    };

    const appendTipoSheetToWorkbook = ({
        workbook,
        nombreHoja,
        tipoClienteIncluido,
        fechaGeneracion,
        fechaInicioReporte,
        fechaFinReporte,
        rows,
        totalSaldoPeriodoPendiente,
        totalDeudaAcumuladaPendiente,
    }) => {
        const encabezado = [
            ['Reporte de Saldos de Cuentas por Cobrar'],
            ['Fecha de generacion', fechaGeneracion],
            ['Fecha inicio', fechaInicioReporte],
            ['Fecha fin', fechaFinReporte],
            ['Tipos de cliente incluidos', tipoClienteIncluido],
            ['Cantidad de registros', rows.length],
            [],
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(encabezado);
        const cuerpo = [
            ['Saldo pendiente del periodo', roundMoney(totalSaldoPeriodoPendiente)],
            ['Saldo acumulado pendiente', roundMoney(totalDeudaAcumuladaPendiente)],
            [],
            ['Cliente', 'Saldo pendiente del periodo', 'Saldo acumulado pendiente'],
            ...rows,
        ];

        XLSX.utils.sheet_add_aoa(worksheet, cuerpo, { origin: `A${encabezado.length + 1}` });
        XLSX.utils.book_append_sheet(workbook, worksheet, nombreHoja);
    };

    const filtrosAplicadosSerializados = JSON.stringify({
        fechaInicio: filtrosAplicados?.fechaInicio || null,
        fechaFin: filtrosAplicados?.fechaFin || null,
    });
    const filtrosActualesSerializados = JSON.stringify({
        fechaInicio,
        fechaFin,
    });
    const botonExcelHabilitado = Boolean(
        reporteData && filtrosAplicados && filtrosAplicadosSerializados === filtrosActualesSerializados
    );

    const handleDescargarExcel = async () => {
        if (!botonExcelHabilitado || !reporteData) return;
        setLoadingReporteExcel(true);
        try {
            const fechaGeneracion = dayjs().format('YYYY-MM-DD HH:mm:ss');
            const timestampArchivo = dayjs().format('YYYYMMDD_HHmmss');
            const variablesBase = {
                fechaInicio: filtrosAplicados.fechaInicio,
                fechaFin: filtrosAplicados.fechaFin,
                idCliente: null,
                idSede: idSedeActual || null,
            };

            const allResult = await apolloClient.query({
                query: GET_REPORTE_CUENTAS_POR_COBRAR,
                variables: { ...variablesBase, tipoCliente: null },
                fetchPolicy: 'network-only'
            });

            const dataExcel = allResult?.data?.reporteCuentasPorCobrar;
            if (!dataExcel) return;

            const rowsFuncionario = buildRowsByTipoForExcel(dataExcel, 'Funcionario');
            const rowsNoFuncionario = buildRowsByTipoForExcel(dataExcel, 'No Funcionario');
            const rowsEvento = buildRowsByTipoForExcel(dataExcel, 'Evento');

            const rawRows = buildRawRowsForExcel(dataExcel);
            const resumenRows = buildResumenRowsForExcel({
                resumenPorTipo: {
                    Funcionario: rowsFuncionario,
                    'No Funcionario': rowsNoFuncionario,
                    Evento: rowsEvento,
                },
            });
            const encabezado = [
                ['Reporte de Saldos de Cuentas por Cobrar'],
                ['Fecha de generacion', fechaGeneracion],
                ['Fecha inicio', filtrosAplicados.fechaInicio],
                ['Fecha fin', filtrosAplicados.fechaFin],
                ['Tipos de cliente incluidos', 'Funcionario, No Funcionario y Evento'],
                ['Cantidad de registros', rawRows.length],
                [],
            ];

            const workbook = XLSX.utils.book_new();
            const worksheetResumen = XLSX.utils.aoa_to_sheet(encabezado);
            XLSX.utils.sheet_add_json(worksheetResumen, resumenRows, { origin: `A${encabezado.length + 1}`, skipHeader: false });
            XLSX.utils.book_append_sheet(workbook, worksheetResumen, 'Resumen');

            appendTipoSheetToWorkbook({
                workbook,
                nombreHoja: 'Funcionario',
                tipoClienteIncluido: 'Funcionario',
                fechaGeneracion,
                fechaInicioReporte: filtrosAplicados.fechaInicio,
                fechaFinReporte: filtrosAplicados.fechaFin,
                rows: rowsFuncionario.rows,
                totalSaldoPeriodoPendiente: rowsFuncionario.totalSaldoPeriodoPendiente,
                totalDeudaAcumuladaPendiente: rowsFuncionario.totalDeudaAcumuladaPendiente,
            });
            appendTipoSheetToWorkbook({
                workbook,
                nombreHoja: 'No Funcionario',
                tipoClienteIncluido: 'No Funcionario',
                fechaGeneracion,
                fechaInicioReporte: filtrosAplicados.fechaInicio,
                fechaFinReporte: filtrosAplicados.fechaFin,
                rows: rowsNoFuncionario.rows,
                totalSaldoPeriodoPendiente: rowsNoFuncionario.totalSaldoPeriodoPendiente,
                totalDeudaAcumuladaPendiente: rowsNoFuncionario.totalDeudaAcumuladaPendiente,
            });
            appendTipoSheetToWorkbook({
                workbook,
                nombreHoja: 'Eventos',
                tipoClienteIncluido: 'Evento',
                fechaGeneracion,
                fechaInicioReporte: filtrosAplicados.fechaInicio,
                fechaFinReporte: filtrosAplicados.fechaFin,
                rows: rowsEvento.rows,
                totalSaldoPeriodoPendiente: rowsEvento.totalSaldoPeriodoPendiente,
                totalDeudaAcumuladaPendiente: rowsEvento.totalDeudaAcumuladaPendiente,
            });

            const worksheetDetalles = XLSX.utils.json_to_sheet(rawRows);
            XLSX.utils.book_append_sheet(workbook, worksheetDetalles, 'Detalle Neto');

            XLSX.writeFile(workbook, `Reporte_Saldos_CuentasxCobrar_${timestampArchivo}.xlsx`);
        } finally {
            setLoadingReporteExcel(false);
        }
    };

    const handleTipoClienteChange = (event, value) => {
        const tipoClienteAnterior = tipoCliente;
        setTipoCliente(value);
        setClienteBusqueda('');
        setClienteBusquedaDebounced('');
        setClientesOptions([]);

        if (!value) {
            // Requisito: "Agrupar por" sólo habilitado si hay Tipo Cliente
            setAgruparPor('Periodo');
        }

        if (value !== tipoClienteAnterior) {
            setClienteSeleccionado(null);
        }
    };

    const handleAgruparPorChange = (event) => {
        const value = event.target.value;
        setAgruparPor(value);
    };

    const handleClienteChange = (event, value) => {
        setClienteSeleccionado(value);
        setClienteBusqueda(value?.nombre || '');
    };

    const handleOpenDetalle = (periodo, tipoClienteDetalle, idClienteOverride = null, nombreOverride = null) => {
        const idClienteToUse = idClienteOverride ?? clienteSeleccionado?.id ?? null;

        setModalDetalle({
            open: true,
            periodo,
            tipoCliente: tipoClienteDetalle,
            idCliente: idClienteToUse,
            nombre: nombreOverride
        });

        fetchDetalle({
            variables: {
                periodo,
                tipoCliente: tipoClienteDetalle,
                fechaInicio,
                fechaFin,
                idCliente: idClienteToUse
            }
        });
    };

    const handleCloseDetalle = () => {
        setModalDetalle({ open: false, periodo: null, tipoCliente: null, idCliente: null, nombre: null });
    };

    // ===================================
    // COMPUTED VALUES
    // ===================================
    const clientesFiltrados = useMemo(() => {
        if (!Array.isArray(clientesOptions)) return [];
        if (!tipoCliente) return [];
        return clientesOptions.filter((c) => c?.tipoCliente === tipoCliente);
    }, [clientesOptions, tipoCliente]);

    const botonFiltrarHabilitado = Boolean(fechaFin && tipoCliente);
    const totalesPorPeriodoUnificados = useMemo(() => {
        const periodos = Array.isArray(reporteData?.totalesPorPeriodo) ? reporteData.totalesPorPeriodo : [];
        const clientes = Array.isArray(reporteData?.totalesPorCliente) ? reporteData.totalesPorCliente : [];

        if (!periodos.length || !clientes.length) return periodos;

        const periodosOrdenados = periodos.map((row) => row.periodo);
        const saldoInicioPorPeriodo = Object.fromEntries(periodosOrdenados.map((periodo) => [periodo, 0]));
        const saldoAcumuladoPorPeriodo = Object.fromEntries(periodosOrdenados.map((periodo) => [periodo, 0]));

        clientes.forEach((cliente) => {
            const detalles = Array.isArray(cliente?.detallesPorPeriodo) ? cliente.detallesPorPeriodo : [];
            if (!detalles.length) return;

            const detalleByPeriodo = new Map(detalles.map((detalle) => [detalle?.periodo, detalle]));
            const detallesOrdenados = [...detalles].sort(
                (a, b) => periodosOrdenados.indexOf(a?.periodo) - periodosOrdenados.indexOf(b?.periodo)
            );
            const saldoInicialCliente = Number(detallesOrdenados[0]?.totalInicio || 0);
            let saldoArrastradoCliente = saldoInicialCliente;

            periodosOrdenados.forEach((periodo) => {
                const detallePeriodo = detalleByPeriodo.get(periodo);

                let saldoInicioClientePeriodo = saldoArrastradoCliente;
                let saldoAcumuladoClientePeriodo = saldoArrastradoCliente;

                if (detallePeriodo) {
                    saldoInicioClientePeriodo = Number(detallePeriodo?.totalInicio || 0);
                    saldoAcumuladoClientePeriodo = Number(detallePeriodo?.acumulado || 0);
                    saldoArrastradoCliente = saldoAcumuladoClientePeriodo;
                }

                saldoInicioPorPeriodo[periodo] = roundMoney(
                    Number(saldoInicioPorPeriodo[periodo] || 0) + Number(saldoInicioClientePeriodo || 0)
                );
                saldoAcumuladoPorPeriodo[periodo] = roundMoney(
                    Number(saldoAcumuladoPorPeriodo[periodo] || 0) + Number(saldoAcumuladoClientePeriodo || 0)
                );
            });
        });

        return periodos.map((row) => {
            const saldoInicio = roundMoney(saldoInicioPorPeriodo[row.periodo] || 0);
            const saldoAcumulado = roundMoney(saldoAcumuladoPorPeriodo[row.periodo] || 0);
            const variacionPeriodo = roundMoney(saldoAcumulado - saldoInicio);

            return {
                ...row,
                totalInicio: roundMoney(saldoInicio),
                acumulado: roundMoney(saldoAcumulado),
                total: roundMoney(variacionPeriodo),
            };
        });
    }, [reporteData]);

    // ===================================
    // SUMMARY & RANKING (SIN GRAFICOS)
    // ===================================
    const clientesDelFiltro = useMemo(() => {
        const clientes = Array.isArray(reporteData?.totalesPorCliente) ? reporteData.totalesPorCliente : [];
        if (!tipoCliente) return clientes;
        return clientes.filter((cliente) => cliente?.tipoCliente === tipoCliente);
    }, [reporteData, tipoCliente]);

    const saldoPeriodoNetoTotal = useMemo(
        () => roundMoney(clientesDelFiltro.reduce((acc, cliente) => acc + getSaldoPeriodoNetoCliente(cliente), 0)),
        [clientesDelFiltro]
    );

    const saldoAcumuladoNetoTotal = useMemo(
        () => roundMoney(clientesDelFiltro.reduce((acc, cliente) => acc + getSaldoHistoricoCliente(cliente), 0)),
        [clientesDelFiltro]
    );

    const saldoPeriodoPendienteTotal = useMemo(
        () => roundMoney(clientesDelFiltro.reduce((acc, cliente) => acc + saldoPendiente(getSaldoPeriodoNetoCliente(cliente)), 0)),
        [clientesDelFiltro]
    );

    const saldoAcumuladoPendienteTotal = useMemo(
        () => roundMoney(clientesDelFiltro.reduce((acc, cliente) => acc + saldoPendiente(getSaldoHistoricoCliente(cliente)), 0)),
        [clientesDelFiltro]
    );

    const formatMoneyAbsolute = (value) =>
        Math.abs(Number(value || 0)).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });

    const buildSaldoCardMeta = ({ value, esAcumulado = false }) => {
        const tienePendiente = Number(value || 0) < 0;

        return {
            titulo: tienePendiente
                ? esAcumulado
                    ? 'Saldo acumulado pendiente hasta fecha de corte'
                    : 'Saldo pendiente del periodo seleccionado'
                : esAcumulado
                    ? 'Saldo acumulado hasta fecha de corte'
                    : 'Saldo del periodo seleccionado',
            colorIcono: tienePendiente ? 'error' : 'success',
            colorValor: tienePendiente ? 'error' : 'success',
            icono: tienePendiente ? 'attach_money' : 'account_balance_wallet',
            backgroundColor: tienePendiente ? '#FFEBEE' : undefined,
            valor: formatMoneyAbsolute(value),
        };
    };

    const cardSaldoPeriodo = useMemo(() => {
        const base = buildSaldoCardMeta({ value: saldoPeriodoNetoTotal, esAcumulado: false });
        if (saldoPeriodoNetoTotal < 0) {
            return { ...base, valor: formatMoneyAbsolute(saldoPeriodoPendienteTotal) };
        }
        return { ...base, valor: formatMoneyAbsolute(saldoPeriodoNetoTotal) };
    }, [saldoPeriodoNetoTotal, saldoPeriodoPendienteTotal]);

    const cardSaldoAcumulado = useMemo(() => {
        const base = buildSaldoCardMeta({ value: saldoAcumuladoNetoTotal, esAcumulado: true });
        if (saldoAcumuladoNetoTotal < 0) {
            return { ...base, valor: formatMoneyAbsolute(saldoAcumuladoPendienteTotal) };
        }
        return { ...base, valor: formatMoneyAbsolute(saldoAcumuladoNetoTotal) };
    }, [saldoAcumuladoNetoTotal, saldoAcumuladoPendienteTotal]);

    const rankingDeudoresVista = useMemo(() => {
        const rows = clientesDelFiltro
            .map((cliente) => {
                const saldoPeriodoNeto = getSaldoPeriodoNetoCliente(cliente);
                const saldoAcumuladoNeto = getSaldoHistoricoCliente(cliente);
                const pendiente = modoRanking === 'PERIODO'
                    ? saldoPendiente(saldoPeriodoNeto)
                    : saldoPendiente(saldoAcumuladoNeto);

                return {
                    idCliente: cliente?.idCliente ?? null,
                    nombre: cliente?.nombre || '',
                    tipoCliente: cliente?.tipoCliente || '',
                    saldoPendiente: roundMoney(pendiente),
                };
            })
            .filter((row) => Number(row.saldoPendiente || 0) > 0)
            .sort((a, b) => Number(b.saldoPendiente || 0) - Number(a.saldoPendiente || 0))
            .slice(0, 20)
            .map((row, index) => ({
                ...row,
                posicion: index + 1,
            }));

        return rows;
    }, [clientesDelFiltro, modoRanking]);

    const footerExplicacionTablaPrincipal = useMemo(() => {
        const totalRegistros = agruparPor === 'Cliente'
            ? (reporteData?.totalesPorCliente || []).length
            : totalesPorPeriodoUnificados.length;

        const itemsPeriodo = [
            {
                columna: 'Periodo',
                descripcion: 'Dado que se aplico un filtro por fechas, este campo muestra el mes que agrupa los saldos.',
                ejemplo: 'FEBRERO-2026',
            },
            {
                columna: 'Saldo acumulado inicio',
                descripcion: 'Es el saldo arrastrado antes de comenzar ese periodo.',
                ejemplo: '-$ 120.000,00',
            },
            {
                columna: 'Total deuda',
                descripcion: 'Suma de deudas generadas dentro del periodo.',
                ejemplo: '-$ 80.000,00',
            },
            {
                columna: 'Total pagos',
                descripcion: 'Suma de pagos aprobados dentro del periodo.',
                ejemplo: '$ 25.000,00',
            },
            {
                columna: 'Beca registrada',
                descripcion: 'Monto de beca/saldo cargado durante ese periodo.',
                ejemplo: '$ 10.000,00',
            },
            {
                columna: 'Beca aplicada',
                descripcion: 'Parte de la beca usada para reducir deuda del periodo.',
                ejemplo: '$ 7.500,00',
            },
            {
                columna: 'Saldo beca',
                descripcion: 'Beca disponible que queda para aplicar en periodos siguientes.',
                ejemplo: '$ 2.500,00',
            },
            {
                columna: 'Saldo periodo neto',
                descripcion: 'Resultado del periodo: deuda + pagos + beca aplicada.',
                ejemplo: '-$ 47.500,00',
            },
            {
                columna: 'Saldo acumulado neto',
                descripcion: 'Saldo final acumulado: saldo inicio + saldo periodo neto.',
                ejemplo: '-$ 167.500,00',
            },
        ];

        const itemsCliente = [
            {
                columna: 'Cliente',
                descripcion: 'Nombre del cliente y su tipo (Funcionario / No Funcionario / Evento).',
                ejemplo: 'JUAN PEREZ - No Funcionario',
            },
            {
                columna: 'Saldo acumulado inicio',
                descripcion: 'Saldo con el que ese cliente llega al inicio del rango filtrado.',
                ejemplo: '-$ 60.000,00',
            },
            {
                columna: 'Total deuda',
                descripcion: 'Total de deuda generada por ese cliente dentro del rango.',
                ejemplo: '-$ 45.000,00',
            },
            {
                columna: 'Total pagos',
                descripcion: 'Total de pagos aprobados del cliente dentro del rango.',
                ejemplo: '$ 20.000,00',
            },
            {
                columna: 'Beca registrada',
                descripcion: 'Beca/saldo cargado al cliente dentro del rango.',
                ejemplo: '$ 15.000,00',
            },
            {
                columna: 'Beca aplicada',
                descripcion: 'Parte de esa beca usada para bajar deuda del cliente.',
                ejemplo: '$ 12.000,00',
            },
            {
                columna: 'Saldo beca',
                descripcion: 'Beca remanente del cliente que todavia puede aplicarse.',
                ejemplo: '$ 3.000,00',
            },
            {
                columna: 'Saldo periodo neto',
                descripcion: 'Resultado del cliente en el rango: deuda + pagos + beca aplicada.',
                ejemplo: '-$ 13.000,00',
            },
            {
                columna: 'Saldo acumulado neto',
                descripcion: 'Saldo total del cliente luego del rango (incluye arrastre).',
                ejemplo: '-$ 73.000,00',
            },
        ];
        const items = agruparPor === 'Cliente' ? itemsCliente : itemsPeriodo;

        return (
            <SoftBox width="100%" display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                <SoftBox sx={{ minWidth: 0 }}>
                    <SoftTypography variant="caption" color="text">
                        Total de registros: {totalRegistros}
                    </SoftTypography>
                    <SoftBox mt={0.5}>
                        {items.map((item) => (
                            <SoftTypography key={item.columna} variant="caption" color="text" display="block" sx={{ lineHeight: 1.45 }}>
                                {`- ${item.columna}: ${item.descripcion} Ejemplo: ${item.ejemplo}.`}
                            </SoftTypography>
                        ))}
                    </SoftBox>
                </SoftBox>
                <SoftTypography variant="caption" color="text">
                    Listo
                </SoftTypography>
            </SoftBox>
        );
    }, [agruparPor, reporteData, totalesPorPeriodoUnificados.length]);
    // TABLE COLUMNS
    // ===================================
    const columnasTablaPrincipal = [
        {
            field: 'periodo',
            headerName: 'Periodo',
            width: '15%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="bold" color="dark">
                    {row.periodo}
                </SoftTypography>
            )
        },
        {
            field: 'totalInicio',
            headerName: 'Saldo acumulado inicio',
            width: '14%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color={row.totalInicio < 0 ? "error" : "success"}>
                    {row.totalInicio.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalDeuda',
            headerName: 'Total Deuda',
            width: '14%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color="error">
                    {(row.totalDeuda).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalPagos',
            headerName: 'Total Pagos',
            width: '14%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color="success">
                    {row.totalPagos.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalBecasRegistradas',
            headerName: 'Beca registrada',
            width: '14%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color="info">
                    {(row.totalBecasRegistradas || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalBecas',
            headerName: 'Beca aplicada',
            width: '14%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color="info">
                    {(row.totalBecas || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'saldoBeca',
            headerName: 'Saldo beca',
            width: '14%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color={(row.saldoBeca || 0) > 0 ? 'warning' : 'text'}>
                    {(row.saldoBeca || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'total',
            headerName: 'Saldo periodo neto',
            width: '14%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="bold" color={row.total < 0 ? "error" : "success"}>
                    {row.total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'acumulado',
            headerName: 'Saldo acumulado neto',
            width: '14%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="bold" color={row.acumulado < 0 ? "error" : "success"}>
                    {row.acumulado.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        }
    ];

    const columnasTablaPorCliente = [
        {
            field: 'nombre',
            headerName: 'Cliente',
            width: '17%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox>
                    <SoftTypography variant="button" fontWeight="medium" color="text">
                        {row.nombre}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="secondary" display="block">
                        {row.tipoCliente}
                    </SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'totalInicio',
            headerName: 'Saldo acumulado inicio',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color={row.totalInicio < 0 ? "error" : "success"}>
                    {row.totalInicio.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalDeuda',
            headerName: 'Total Deuda',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color="error">
                    {Math.abs(row.totalDeuda).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalPagos',
            headerName: 'Total Pagos',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color="success">
                    {row.totalPagos.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalBecasRegistradas',
            headerName: 'Beca registrada',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color="info">
                    {(row.totalBecasRegistradas || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalBecas',
            headerName: 'Beca aplicada',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color="info">
                    {(row.totalBecas || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'saldoBeca',
            headerName: 'Saldo beca',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" color={(row.saldoBeca || 0) > 0 ? 'warning' : 'text'}>
                    {(row.saldoBeca || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'total',
            headerName: 'Saldo periodo neto',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="bold" color={row.total < 0 ? "error" : "success"}>
                    {row.total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'acumulado',
            headerName: 'Saldo acumulado neto',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="bold" color={row.acumulado < 0 ? "error" : "success"}>
                    {row.acumulado.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        }
    ];

    const columnasSubtablaPorPeriodo = [
        {
            field: 'periodo',
            headerName: 'Periodo',
            width: '15%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="medium" color="dark">
                    {row.periodo}
                </SoftTypography>
            )
        },
        {
            field: 'totalInicio',
            headerName: 'Saldo inicio',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color={row.totalInicio < 0 ? "error" : "text"}>
                    {row.totalInicio.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalDeuda',
            headerName: 'Deudas',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="error">
                    {Math.abs(row.totalDeuda).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalPagos',
            headerName: 'Pagos',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="success">
                    {row.totalPagos.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalBecasRegistradas',
            headerName: 'Beca registrada',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="info">
                    {(row.totalBecasRegistradas || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'totalBecas',
            headerName: 'Beca aplicada',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="info">
                    {(row.totalBecas || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'saldoBeca',
            headerName: 'Saldo beca',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color={(row.saldoBeca || 0) > 0 ? 'warning' : 'text'}>
                    {(row.saldoBeca || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'total',
            headerName: 'Saldo periodo neto',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" fontWeight="bold" color={row.total < 0 ? "error" : "text"}>
                    {row.total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'acumulado',
            headerName: 'Saldo acumulado neto',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" fontWeight="bold" color={row.acumulado < 0 ? "error" : "text"}>
                    {row.acumulado.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        }
    ];

    const columnasSubtabla = [
        {
            field: 'tipoCliente',
            headerName: 'Tipo Cliente',
            width: '10%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="medium" color="dark">
                    {row.tipoCliente}
                </SoftTypography>
            )
        },
        {
            field: 'totalInicio',
            headerName: 'Saldo acumulado inicio',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color={row.totalInicio < 0 ? "error" : "text"}>
                    {row.totalInicio.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'deudas',
            headerName: 'Deudas',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="error">
                    {Math.abs(row.deudas).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'pagos',
            headerName: 'Pagos',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="success">
                    {row.pagos.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'becasRegistradas',
            headerName: 'Beca registrada',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="info">
                    {(row.becasRegistradas || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'becas',
            headerName: 'Beca aplicada',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="info">
                    {(row.becas || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'saldoBeca',
            headerName: 'Saldo beca',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color={(row.saldoBeca || 0) > 0 ? 'warning' : 'text'}>
                    {(row.saldoBeca || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'total',
            headerName: 'Saldo periodo neto',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" fontWeight="bold" color={row.total < 0 ? "error" : "text"}>
                    {row.total.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        },
        {
            field: 'acumulado',
            headerName: 'Saldo acumulado neto',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" fontWeight="bold" color={row.acumulado < 0 ? "error" : "text"}>
                    {row.acumulado.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        }
    ];

    const columnasRanking = [
        {
            field: 'posicion',
            headerName: '#',
            width: '10%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="bold" color="secondary">
                    {row.posicion}
                </SoftTypography>
            )
        },
        {
            field: 'idCliente',
            headerName: 'ID',
            width: '15%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox>
                    <SoftTypography variant="button" fontWeight="medium" color="text">
                        {row.idCliente}
                    </SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'nombre',
            headerName: 'Cliente',
            width: '40%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftBox>
                    <SoftTypography variant="button" fontWeight="medium" color="text">
                        {row.nombre}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="secondary" display="block">
                        {row.tipoCliente}
                    </SoftTypography>
                </SoftBox>
            )
        },
        {
            field: 'saldoPendiente',
            headerName: modoRanking === 'PERIODO' ? 'Pendiente periodo' : 'Pendiente acumulado',
            // width: '25%',
            type: 'number',
            align: 'right',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-end" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="button" fontWeight="bold" color="error">
                    {Math.abs(row.saldoPendiente).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}
                </SoftTypography>
            )
        }
    ];

    const columnasDetalle = [
        {
            field: 'fecha',
            headerName: 'Fecha',
            width: '15%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="text">
                    {row.fecha}
                </SoftTypography>
            )
        },
        {
            field: 'nombre',
            headerName: 'Nombre',
            width: '30%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="text">
                    {row.nombre}
                </SoftTypography>
            )
        },
        {
            field: 'tipoCliente',
            headerName: 'Tipo Cliente',
            // width: '15%',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="secondary">
                    {row.tipoCliente}
                </SoftTypography>
            )
        },
        {
            field: 'responsable',
            headerName: 'Responsable',
            width: '20%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color="text">
                    {tiposDeResponsable[row.responsable == 0 ? 5 : row.responsable]}
                </SoftTypography>
            )
        },
        {
            field: 'deuda',
            headerName: 'Deuda',
            // width: '15%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color={row.deuda < 0 ? "error" : "text"}>
                    {row.deuda !== 0 ? Math.abs(row.deuda).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' }) : '-'}
                </SoftTypography>
            )
        },
        {
            field: 'pago',
            headerName: 'Pago',
            // width: '15%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color={row.pago > 0 ? "success" : "text"}>
                    {row.pago !== 0 ? row.pago.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' }) : '-'}
                </SoftTypography>
            )
        },
        {
            field: 'beca',
            headerName: 'Beca',
            // width: '15%',
            type: 'number',
            renderHeader: (column, isCurrentSort, direction) => (
                <SoftBox display="flex" alignItems="center" justifyContent="flex-start" gap={1} sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                        <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="dark" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }} title={column.headerName}>
                            {column.headerName}
                        </SoftTypography>
                    </SoftBox>
                    {isCurrentSort && direction ? (
                        <Icon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }}>
                            {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        </Icon>
                    ) : (
                        <Icon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 0.8 } }}>
                            import_export
                        </Icon>
                    )}
                </SoftBox>
            ),
            renderCell: (row) => (
                <SoftTypography variant="caption" color={(row.beca || 0) > 0 ? "info" : "text"}>
                    {(row.beca || 0) !== 0 ? (row.beca || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' }) : '-'}
                </SoftTypography>
            )
        }
    ];

    // ===================================
    // RENDER EXPANDIBLE ROW
    // ===================================
    const renderExpandedRow = (row) => {
        if (!row.detallesPorTipo || row.detallesPorTipo.length === 0) return null;

        // Agregar handler de click a cada fila
        const dataConClick = row.detallesPorTipo.map(detalle => ({
            ...detalle,
            onClick: () => handleOpenDetalle(row.periodo, detalle.tipoCliente)
        }));

        return (
            <SoftBox p={2} bgcolor="grey.100" borderRadius="lg">
                <SoftTypography variant="h6" mb={2}>Detalle por Tipo de Cliente</SoftTypography>
                <DataTableCustom
                    columns={columnasSubtabla}
                    data={dataConClick}
                    showTotalEntries={false}
                    entriesPerPage={false}
                    canSearch={false}
                    onRowClick={(detalle) => handleOpenDetalle(row.periodo, detalle.tipoCliente)}
                    showActions={false}
                    headerStyles={{
                        backgroundColor: "#ffffff",
                        borderBottom: "1.5px solid #070606ff",
                        py: 1,
                        "& .MuiTypography-root": {
                            fontWeight: 600,
                            color: "#344767",
                            fontSize: "0.7rem",
                            letterSpacing: 0.2,
                        },
                    }}
                />
            </SoftBox>
        );
    };

    const renderExpandedRowCliente = (row) => {
        if (!row.detallesPorPeriodo || row.detallesPorPeriodo.length === 0) return null;

        const dataConClick = row.detallesPorPeriodo.map(detalle => ({
            ...detalle,
            onClick: () => handleOpenDetalle(detalle.periodo, tipoCliente, row.idCliente, row.nombre)
        }));

        return (
            <SoftBox p={2} bgcolor="grey.100" borderRadius="lg">
                <SoftTypography variant="h6" mb={2}>Detalle por Período</SoftTypography>
                <DataTableCustom
                    columns={columnasSubtablaPorPeriodo}
                    data={dataConClick}
                    showTotalEntries={false}
                    entriesPerPage={false}
                    canSearch={false}
                    onRowClick={(detalle) => handleOpenDetalle(detalle.periodo, tipoCliente, row.idCliente, row.nombre)}
                    showActions={false}
                    headerStyles={{
                        backgroundColor: "#ffffff",
                        borderBottom: "1.5px solid #070606ff",
                        py: 1,
                        "& .MuiTypography-root": {
                            fontWeight: 600,
                            color: "#344767",
                            fontSize: "0.7rem",
                            letterSpacing: 0.2,
                        },
                    }}
                />
            </SoftBox>
        );
    };

    // ===================================
    // RENDER
    // ===================================
    return (
        <SoftBox py={3}>
            <Card>
                <SoftBox p={3}>
                    {/* FILTROS */}
                    <SoftBox
                        p={3}
                        mb={3}
                        bgcolor="grey.100"
                        borderRadius="lg"
                        sx={{ border: '1px solid', borderColor: 'grey.300' }}
                    >
                        <SoftTypography variant="h6" mb={2}>Filtros</SoftTypography>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} md={2}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        Fecha de corte *
                                    </SoftTypography>
                                </SoftBox>
                                <SoftInput
                                    type="date"
                                    value={fechaFin}
                                    onChange={(e) => setFechaFin(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        Tipo Cliente *
                                    </SoftTypography>
                                </SoftBox>
                                <Autocomplete
                                    value={tipoCliente}
                                    onChange={handleTipoClienteChange}
                                    options={['Funcionario', 'No Funcionario']}
                                    renderInput={(params) => <TextField {...params} placeholder="Seleccione" />}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        Agrupar por
                                    </SoftTypography>
                                </SoftBox>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    value={agruparPor}
                                    onChange={handleAgruparPorChange}
                                    disabled={!tipoCliente}
                                >
                                    <MenuItem value="Periodo">Periodo</MenuItem>
                                    <MenuItem value="Cliente">Cliente</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        Cliente
                                    </SoftTypography>
                                </SoftBox>
                                <Autocomplete
                                    value={clienteSeleccionado}
                                    onChange={handleClienteChange}
                                    open={clienteAutocompleteOpen}
                                    onOpen={() => setClienteAutocompleteOpen(true)}
                                    onClose={() => setClienteAutocompleteOpen(false)}
                                    inputValue={clienteBusqueda}
                                    onInputChange={(event, value, reason) => {
                                        setClienteBusqueda(value || '');
                                        if (reason === 'clear' || reason === 'input') {
                                            setClienteSeleccionado(null);
                                        }
                                    }}
                                    options={clientesFiltrados}
                                    filterOptions={(options) => options}
                                    getOptionLabel={(option) => option.nombre}
                                    renderInput={(params) => <TextField {...params} placeholder="Todos" />}
                                    loading={loadingClientes}
                                    noOptionsText={
                                        !tipoCliente
                                            ? 'Seleccione Tipo Cliente'
                                            : clienteBusquedaDebounced.length < 2
                                                ? 'Escriba al menos 2 caracteres'
                                                : 'Sin resultados'
                                    }
                                    size="small"
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                        && option.esEvento === value.esEvento
                                        && option.tipoCliente === value.tipoCliente
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <SoftButton
                                    variant="gradient"
                                    color="info"
                                    fullWidth
                                    onClick={handleFiltrar}
                                    disabled={!botonFiltrarHabilitado || loadingReporte}
                                >
                                    {loadingReporte ? <CircularProgress size={20} color="inherit" /> : 'Filtrar'}
                                </SoftButton>
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <Tooltip title={!botonExcelHabilitado ? "Ejecute el reporte para el rango de fechas seleccionado" : "Descargar Excel (saldo del periodo y deuda acumulada)"} placement='top'>
                                    <SoftButton
                                        color="success"
                                        iconOnly
                                        circular
                                        fullWidth
                                        onClick={handleDescargarExcel}
                                        disabled={!botonExcelHabilitado || loadingReporte || loadingReporteExcel}
                                    >
                                        {loadingReporteExcel ? <CircularProgress size={20} color="inherit" /> : <Icon>table_view</Icon>}
                                    </SoftButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </SoftBox>

                    {/* LOADING */}
                    {loadingReporte && (
                        <SoftBox display="flex" justifyContent="center" p={5}>
                            <CircularProgress />
                        </SoftBox>
                    )}

                    {/* CONTENIDO DEL REPORTE */}
                    {!loadingReporte && reporteData && (
                        <>
                            <Grid container spacing={2} mb={3}>
                                {/* <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2, border: "1px solid #e6e6e6", backgroundColor: cardSaldoPeriodo.backgroundColor }}>
                                        <SoftBox display="flex" alignItems="center" gap={1}>
                                            <Icon color={cardSaldoPeriodo.colorIcono}>{cardSaldoPeriodo.icono}</Icon>
                                            <SoftTypography variant="caption" color="text" fontWeight="bold">
                                                {cardSaldoPeriodo.titulo}
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftTypography variant="h6" color={cardSaldoPeriodo.colorValor} mt={0.5}>
                                            {cardSaldoPeriodo.valor}
                                        </SoftTypography>
                                    </Card>
                                </Grid> */}
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2, border: "1px solid #e6e6e6", backgroundColor: cardSaldoAcumulado.backgroundColor }}>
                                        <SoftBox display="flex" alignItems="center" gap={1}>
                                            <Icon color={cardSaldoAcumulado.colorIcono}>{cardSaldoAcumulado.icono}</Icon>
                                            <SoftTypography variant="caption" color="text" fontWeight="bold">
                                                {cardSaldoAcumulado.titulo}
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftTypography variant="h6" color={cardSaldoAcumulado.colorValor} mt={0.5}>
                                            {cardSaldoAcumulado.valor}
                                        </SoftTypography>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* TABLA PRINCIPAL */}
                            <SoftBox mb={3}>
                                <SoftTypography variant="h6" mb={2}>
                                    {agruparPor === 'Cliente' ? 'Saldos por cliente (periodo y acumulado)' : 'Saldos por periodo (periodo y acumulado)'}
                                </SoftTypography>
                                <SoftTypography variant="caption" color="text" display="block" mb={2}>
                                    Saldo del periodo: considera solo movimientos dentro del rango. Saldo acumulado: incluye arrastre previo. Valores netos: negativos indican deuda y positivos saldo a favor.
                                </SoftTypography>
                                <DataTableCustom
                                    columns={agruparPor === 'Cliente' ? columnasTablaPorCliente : columnasTablaPrincipal}
                                    data={agruparPor === 'Cliente' ? (reporteData.totalesPorCliente || []) : totalesPorPeriodoUnificados}
                                    canSearch
                                    expandedRowRender={agruparPor === 'Cliente' ? renderExpandedRowCliente : renderExpandedRow}
                                    showActions={false}
                                    footerContent={footerExplicacionTablaPrincipal}
                                    headerStyles={{
                                        backgroundColor: "#ffffff",
                                        borderBottom: "1.5px solid #070606ff",
                                        py: 1,
                                        "& .MuiTypography-root": {
                                            fontWeight: 600,
                                            color: "#344767",
                                            fontSize: "0.7rem",
                                            letterSpacing: 0.2,
                                        },
                                    }}
                                />
                            </SoftBox>

                            {/* RANKING DE DEUDORES */}
                            <Grid container spacing={3} my={3}>
                                <Grid item xs={12}>
                                    <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                                        <SoftTypography variant="h6">Ranking de deudores (Top 20)</SoftTypography>
                                        <TextField
                                            select
                                            size="small"
                                            value={modoRanking}
                                            onChange={(e) => setModoRanking(e.target.value)}
                                            sx={{ minWidth: 300 }}
                                        >
                                            <MenuItem value="PERIODO">Mostrar por periodo seleccionado</MenuItem>
                                            <MenuItem value="ACUMULADO">Mostrar acumulado total</MenuItem>
                                        </TextField>
                                    </SoftBox>
                                    <DataTableCustom
                                        columns={columnasRanking}
                                        data={rankingDeudoresVista}
                                        showTotalEntries={false}
                                        entriesPerPage={false}
                                        canSearch
                                        showActions={false}
                                        headerStyles={{
                                            backgroundColor: "#ffffff",
                                            borderBottom: "1.5px solid #070606ff",
                                            py: 1,
                                            "& .MuiTypography-root": {
                                                fontWeight: 600,
                                                color: "#344767",
                                                fontSize: "0.7rem",
                                                letterSpacing: 0.2,
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {/* MENSAJE INICIAL */}
                    {!loadingReporte && !reporteData && (
                        <SoftBox display="flex" justifyContent="center" alignItems="center" p={5}>
                            <SoftTypography variant="h6" color="text">
                                Seleccione los filtros y presione &quot;Filtrar&quot; para ver el reporte
                            </SoftTypography>
                        </SoftBox>
                    )}
                </SoftBox>
            </Card>

            {/* MODAL DE DETALLE */}
            <Modal
                open={modalDetalle.open}
                onClose={handleCloseDetalle}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={modalDetalle.open}>
                    <SoftBox
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: 24,
                            p: 4
                        }}
                    >
                        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <SoftTypography variant="h5" fontWeight="bold">
                                Detalle de Movimientos - {modalDetalle.tipoCliente} - {modalDetalle.periodo}{modalDetalle.nombre ? ` - ${modalDetalle.nombre}` : ''}
                            </SoftTypography>
                            <Icon
                                sx={{ cursor: 'pointer', fontSize: '1.5rem' }}
                                onClick={handleCloseDetalle}
                            >
                                close
                            </Icon>
                        </SoftBox>

                        {loadingDetalle ? (
                            <SoftBox display="flex" justifyContent="center" p={5}>
                                <CircularProgress />
                            </SoftBox>
                        ) : (
                            <DataTableCustom
                                columns={columnasDetalle}
                                data={detalleData?.detalleMovimientos || []}
                                canSearch
                                showActions={false}
                                headerStyles={{
                                    backgroundColor: "#ffffff",
                                    borderBottom: "1.5px solid #070606ff",
                                    py: 1,
                                    "& .MuiTypography-root": {
                                        fontWeight: 600,
                                        color: "#344767",
                                        fontSize: "0.7rem",
                                        letterSpacing: 0.2,
                                    },
                                }}
                            />
                        )}
                    </SoftBox>
                </Fade>
            </Modal>

        </SoftBox>
    );
}

export default ReporteCuentasxCobrar;

